import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BadgeCheck } from "lucide-react";

// Mock order data — rotate through these to create live social proof.
const ORDERS = [
  { name: "Marcus", location: "Austin, TX", product: "The Faceless Marketing Bundle", img: "/assets/cover-social-media.jpg" },
  { name: "Priya", location: "London, UK", product: "The Faceless Marketing Bundle", img: "/assets/cover-reels.jpg" },
  { name: "Chloe", location: "Sydney, AU", product: "The Faceless Marketing Bundle", img: "/assets/cover-digital-marketing.jpg" },
  { name: "Jordan", location: "Toronto, CA", product: "The Faceless Marketing Bundle", img: "/assets/cover-ai-design.jpg" },
  { name: "Sofia", location: "Miami, FL", product: "The Faceless Marketing Bundle", img: "/assets/cover-etsy-pinterest.jpg" },
  { name: "Liam", location: "Dublin, IE", product: "The Faceless Marketing Bundle", img: "/assets/cover-social-media.jpg" },
  { name: "Aaliyah", location: "Atlanta, GA", product: "The Faceless Marketing Bundle", img: "/assets/cover-reels.jpg" },
  { name: "Noah", location: "Denver, CO", product: "The Faceless Marketing Bundle", img: "/assets/faceless-vault.jpg" },
  { name: "Emma", location: "Berlin, DE", product: "The Faceless Marketing Bundle", img: "/assets/cover-digital-marketing.jpg" },
  { name: "Diego", location: "Los Angeles, CA", product: "The Faceless Marketing Bundle", img: "/assets/cover-ai-design.jpg" },
  { name: "Isla", location: "Auckland, NZ", product: "The Faceless Marketing Bundle", img: "/assets/cover-etsy-pinterest.jpg" },
  { name: "Ethan", location: "Chicago, IL", product: "The Faceless Marketing Bundle", img: "/assets/faceless-vault.jpg" },
];

const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const timeAgo = () => {
  const opts = ["About 10 seconds ago", "A minute ago", "2 minutes ago", "4 minutes ago", "8 minutes ago", "12 minutes ago", "18 minutes ago"];
  return opts[rand(0, opts.length - 1)];
};

const VISIBLE_MS = 6000; // how long each toast stays on screen
const MIN_GAP_MS = 8000; // 8s
const MAX_GAP_MS = 15000; // 15s
const FIRST_DELAY_MS = 5000; // first toast after 5s

export default function SocialProofPopup() {
  const [current, setCurrent] = useState(null);
  const idx = useRef(rand(0, ORDERS.length - 1));
  const closed = useRef(false);
  const timers = useRef([]);

  const clearTimers = () => {
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };

  const showNext = () => {
    if (closed.current) return;
    const order = ORDERS[idx.current % ORDERS.length];
    idx.current += 1;
    setCurrent({ ...order, ago: timeAgo(), key: Date.now() });

    // Hide after VISIBLE_MS, then schedule the next one.
    timers.current.push(
      setTimeout(() => {
        setCurrent(null);
        timers.current.push(setTimeout(showNext, rand(MIN_GAP_MS, MAX_GAP_MS)));
      }, VISIBLE_MS)
    );
  };

  useEffect(() => {
    timers.current.push(setTimeout(showNext, FIRST_DELAY_MS));
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    closed.current = true;
    clearTimers();
    setCurrent(null);
  };

  return (
    <div className="fixed bottom-5 left-5 z-[60] pointer-events-none" data-testid="social-proof-container">
      <AnimatePresence mode="wait">
        {current && (
          <motion.div
            key={current.key}
            initial={{ opacity: 0, x: -60, y: 10 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="pointer-events-auto relative flex items-center gap-3 w-[320px] max-w-[calc(100vw-2.5rem)] bg-[#121212]/95 backdrop-blur-md border border-zinc-800 shadow-2xl shadow-black/60 rounded-xl p-3 pr-9"
            data-testid="social-proof-toast"
          >
            <div className="w-12 h-12 shrink-0 rounded-lg overflow-hidden bg-[#1C1C1E] border border-zinc-700">
              <img src={current.img} alt={current.product} className="w-full h-full object-cover" loading="lazy" />
            </div>

            <div className="min-w-0">
              <p className="text-sm text-white leading-tight truncate" data-testid="social-proof-name">
                <span className="font-semibold">{current.name}</span>
                <span className="text-zinc-400"> in {current.location}</span>
              </p>
              <p className="text-xs text-zinc-300 leading-tight mt-0.5 truncate">
                purchased <span className="text-[#D4AF37]">{current.product}</span>
              </p>
              <p className="flex items-center gap-1 text-[11px] text-zinc-500 mt-1">
                <BadgeCheck className="w-3 h-3 text-[#D4AF37]" />
                {current.ago}
                <span className="mx-1">·</span>
                <span className="text-emerald-400">Verified</span>
              </p>
            </div>

            <button
              onClick={handleClose}
              aria-label="Dismiss notification"
              data-testid="social-proof-close"
              className="absolute top-2 right-2 text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
